<template>
  <v-card color="card" class="elevation-2 rounded-lg">
    <v-sheet color="primary">
      <v-card-title class="pa-2">
        <div class="text-h6 ml-2 white--text">
          {{ getLabel('app_access') }}
        </div>
      </v-card-title>
    </v-sheet>
    <v-card-text class="px-0">
      <v-form ref="form">
        <v-row class="mx-4">
          <v-select
            v-model="selectedRoles"
            class="mb-8 mt-6"
            :items="roles"
            :label="getLabel('app_access')"
            clearable
            multiple
            hide-details
          />
        </v-row>
      </v-form>
      <v-divider />
    </v-card-text>
    <v-card-actions class="pr-4 pb-5 pt-1">
      <v-spacer />
      <v-btn
        color="warning"
        dark
        @click="reset()"
      >
        <v-icon class="mr-2">mdi-broom</v-icon>
        {{ getLabel("clean") }}
      </v-btn>
      <v-btn
        class="ml-4"

        color="green darken-1"
        dark
        @click="saveAppAccesses()"
      >
        <v-icon class="mr-2">mdi-content-save</v-icon>
        {{ getLabel("save") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { ref } from '@vue/composition-api';
import { useMutation } from '@vue/apollo-composable';
import { useRoute } from 'vue2-helpers/vue-router';

import { getGraphqlSchema } from '@/graphql/index';
import { useMainStore } from '@/store/index';
import { getLabel, EventBus } from '@/utils/index';

export default {
  setup () {
    const store = useMainStore();

    const route = useRoute();
    const rolesValues = store.roles;
    const roles = [];

    // Only show roles that have labels
    // if a new role is added is necessary to add a label
    for (const value of rolesValues) {
      const label = getLabel(`${value}_application`);

      if (label === `${value}_application`) {
        continue;
      }

      roles.push({
        text: getLabel(`${value}_application`),
        value
      });
    }

    const object = route.name === 'manageProfile' ? 'collaborator' : 'group';

    const selectedRoles = ref(store[object].roles);

    const reset = () => {
      selectedRoles.value = store[object].roles;
    };

    let { defaultWorkspace } = store[object];
    let { readWorkspaces } = store[object];
    let { writeWorkspaces } = store[object];

    // Update Collaborator Roles
    const { mutate: mutationUpdateCollaboratorRoles, onError: errorUpdateCollaborator, onDone: doneUpdateCollaborator } = useMutation(
      getGraphqlSchema('updateCollaboratorRoles'),
      () => ({
        variables:
          {
            uuid: store.collaborator.uuid,
            roles: selectedRoles.value,
            readWorkspaces: readWorkspaces,
            writeWorkspaces: writeWorkspaces,
            defaultWorkspace: defaultWorkspace && defaultWorkspace.code ? defaultWorkspace.code : defaultWorkspace
          }
      })
    );

    errorUpdateCollaborator(() => {
      store.loading = false;

      EventBus.$emit('show-notifications', [{
        message: getLabel('error_updating_app_access'),
        type: 'danger'
      }]);
    });

    doneUpdateCollaborator(result => {
      store.collaborator = { ...store.collaborator, ...result.data.updateCollaborator };

      selectedRoles.value = result.data.updateCollaborator.roles;
      EventBus.$emit('show-notifications', [{
        message: getLabel('updated_app_access'),
        type: 'success'
      }]);

      store.loading = false;
    });

    /** ******************************************* */

    // Update Group Roles
    const { mutate: mutationUpdateGroupRoles, onError: errorUpdateGroup, onDone: doneUpdateGroup } = useMutation(
      getGraphqlSchema('updateGroupRoles'),
      () => ({
        variables:
          {
            uuid: store.group.uuid,
            roles: selectedRoles.value,
            readWorkspaces: readWorkspaces,
            writeWorkspaces: writeWorkspaces,
            defaultWorkspace: defaultWorkspace && defaultWorkspace.code ? defaultWorkspace.code : defaultWorkspace
          }
      })
    );

    errorUpdateGroup(() => {
      store.loading = false;
      EventBus.$emit('show-notifications', [{
        message: getLabel('error_updating_app_access'),
        type: 'danger'
      }]);
    });

    doneUpdateGroup(result => {
      store.group = { ...store.group, ...result.data.updateGroup };

      selectedRoles.value = result.data.updateGroup.roles;
      EventBus.$emit('show-notifications', [{
        message: getLabel('updated_app_access'),
        type: 'success'
      }]);

      store.loading = false;
    });

    /** ********************************************* */

    const restrictWorkspacesByRoles = () => {
      if (selectedRoles.value.includes('admin')) {
        return;
      }

      if (defaultWorkspace && !selectedRoles.value.includes(defaultWorkspace.project)) {
        defaultWorkspace = null;
      }

      if (!readWorkspaces.includes('*')) {
        readWorkspaces = readWorkspaces.filter(rw => {
          // CRM Recruiter role must be able to have permissions to read and write on collaborator workspace. For more details ask Rosty
          if (rw === 'collaborator' && selectedRoles.value.includes('recruiter')) {
            return true;
          }

          return store.workspaces.some(w => w.code === rw && selectedRoles.value.includes(w.project))
        });
      };

      if (!writeWorkspaces.includes('*')) {
        writeWorkspaces = writeWorkspaces.filter(ww => {
          // CRM Recruiter role must be able to have permissions to read and write on collaborator workspace. For more details ask Rosty
          if (ww === 'collaborator' && selectedRoles.value.includes('recruiter')) {
            return true;
          }

          return store.workspaces.some(w => w.code === ww && selectedRoles.value.includes(w.project))
        });
      };
    }

    const saveAppAccesses = () => {
      store.loading = true;

      defaultWorkspace = store.workspaces.find(w => store[object].defaultWorkspace && w.code === store[object].defaultWorkspace);
      readWorkspaces = store[object].readWorkspaces;
      writeWorkspaces = store[object].writeWorkspaces;

      restrictWorkspacesByRoles();

      EventBus.$emit('cancel-workspaces-edit');

      if (object === 'collaborator') {
        mutationUpdateCollaboratorRoles();
      } else {
        mutationUpdateGroupRoles();
      }
    };

    return { getLabel, roles, selectedRoles, reset, saveAppAccesses };
  }

};

</script>
